import { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from '@/context/RouterContext';
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/context/I18nContext';
import { teamsApi } from '@/api/teams';
import { MaterialSubTabBar, MATERIAL_SUBTABS, type MaterialSubTab } from './MaterialSubTabBar';
import { EnginesTab } from './EnginesTab';
import { TyresTab } from './TyresTab';
import { ChassisTab } from './ChassisTab';
import { CarburetorsTab } from './CarburetorsTab';
import { AxlesTab } from './AxlesTab';
import { HubsTab } from './HubsTab';
import { SparkPlugsTab } from './SparkPlugsTab';
import type { Team } from '@/types/team';

const MATERIAL_TAB_ORDER: Record<MaterialSubTab, number> = Object.fromEntries(
  MATERIAL_SUBTABS.map((t, i) => [t, i])
) as Record<MaterialSubTab, number>;

const WRITABLE_ROLES = new Set(['owner', 'manage', 'write']);

export function MaterialsPage() {
  const { params, navigate } = useRouter();
  const { user } = useAuth();
  const { t } = useI18n();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(params.teamId || '');

  // Extract subtab from params (set by route pattern)
  const currentSubTab = (params.subtab as MaterialSubTab) || 'engines';

  useEffect(() => {
    teamsApi.listMyTeams()
      .then((res: Team[]) => {
        const list = res || [];
        setTeams(list);
        if (list.length === 0) return;

        const hasParamTeam = params.teamId && list.some(t => t.id === params.teamId);
        const targetTeamId = hasParamTeam ? params.teamId! : list[0].id;
        setSelectedTeamId(targetTeamId);

        if (params.teamId !== targetTeamId || !params.subtab) {
          navigate(`#/inventory/${targetTeamId}/${currentSubTab}`, true);
        }
      })
      .catch(console.error);
  }, []);

  // Sync selectedTeamId when URL params change (e.g., browser back/forward)
  useEffect(() => {
    if (params.teamId && params.teamId !== selectedTeamId) {
      if (teams.some(t => t.id === params.teamId)) {
        setSelectedTeamId(params.teamId);
      }
    }
  }, [params.teamId, teams, selectedTeamId]);

  // If user navigates to #/inventory without teamId while teams are already loaded
  useEffect(() => {
    if (teams.length > 0 && !params.teamId) {
      const targetTeamId = (selectedTeamId && teams.some(t => t.id === selectedTeamId))
        ? selectedTeamId
        : teams[0].id;
      setSelectedTeamId(targetTeamId);
      navigate(`#/inventory/${targetTeamId}/${currentSubTab}`, true);
    }
  }, [params.teamId, teams, selectedTeamId, currentSubTab, navigate]);

  const selectedTeam = useMemo(
    () => teams.find(t => t.id === selectedTeamId) ?? null,
    [teams, selectedTeamId]
  );

  // Determine write access based on the user's role in the selected team
  const canWrite = useMemo(() => {
    if (!selectedTeam) return false;
    // Owner is always writable
    if (selectedTeam.ownerId === user?.id) return true;
    // Check role from listMyTeams response
    return WRITABLE_ROLES.has(selectedTeam.role ?? '');
  }, [selectedTeam, user?.id]);
  
  const prevTabRef = useRef<MaterialSubTab>(currentSubTab);
  const directionRef = useRef(1);

  if (currentSubTab !== prevTabRef.current) {
    directionRef.current = MATERIAL_TAB_ORDER[currentSubTab] > MATERIAL_TAB_ORDER[prevTabRef.current] ? 1 : -1;
    prevTabRef.current = currentSubTab;
  }

  const renderTab = () => {
    const tabProps = {
      teamId: selectedTeamId,
      readOnly: !canWrite,
    };
    switch (currentSubTab) {
      case 'engines': return <EnginesTab {...tabProps} />;
      case 'tyres': return <TyresTab {...tabProps} />;
      case 'chassis': return <ChassisTab {...tabProps} />;
      case 'carburetors': return <CarburetorsTab {...tabProps} />;
      case 'axles': return <AxlesTab {...tabProps} />;
      case 'hubs': return <HubsTab {...tabProps} />;
      case 'spark-plugs': return <SparkPlugsTab {...tabProps} />;
    }
  };

  return (
    <div className="w-full">
      {/* Team selector */}
      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs font-medium text-text-secondary shrink-0">
          {t('common.team')}
        </label>
        <select
          value={selectedTeamId}
          onChange={e => {
            const newTeamId = e.target.value;
            setSelectedTeamId(newTeamId);
            navigate(`#/inventory/${newTeamId}/${currentSubTab}`);
          }}
          className="input-field py-1.5 px-3 text-sm min-w-[180px] cursor-pointer"
        >
          {teams.length === 0 && (
            <option value="">{t('common.loading')}</option>
          )}
          {teams.map(team => (
            <option key={team.id} value={team.id}>{team.name}</option>
          ))}
        </select>
        {selectedTeam && !canWrite && (
          <span className="text-xs text-text-muted italic">
            {t('materials.readOnly')}
          </span>
        )}
      </div>

      <MaterialSubTabBar
        active={currentSubTab}
        onChange={(st) => {
          if (selectedTeamId) {
            navigate(`#/inventory/${selectedTeamId}/${st}`);
          } else {
            navigate(`#/inventory/${st}`);
          }
        }}
      />

      <div className="relative">
        <AnimatePresence mode="popLayout" custom={directionRef.current}>
          <motion.div
            key={`${currentSubTab}-${selectedTeamId}`}
            custom={directionRef.current}
            initial={{ x: directionRef.current * 10, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: directionRef.current * -10, opacity: 0, transition: { duration: 0.1 } }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {selectedTeamId ? renderTab() : (
              <div className="card p-8 text-center text-text-muted text-sm">
                {t('materials.selectTeam')}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
